import type { ComponentType } from 'react'
import {
  ANNOUNCED_FEATURES,
  PREVIOUS_FEATURES,
} from 'tailwind-v4/blocks/feature-announcement'

/** The shape of one announced feature. See the note on `FEATURES`. */
export interface WhatsNewFeature {
  id:
    | 'smart-seating'
    | 'table-ordering'
    | 'square-rewards'
    | 'wallets'
    | 'phone-bookings'
    | 'ticketed-events'
  name: string
  line: string
  /** Two or three sentences. See the note on the package's own type. */
  summary: string
  art: ComponentType
}

/**
 * The announced features, with their type restated.
 *
 * The package's block barrels use extensionless relative imports, which
 * Connect's `moduleResolution: nodenext` cannot follow (341 pre-existing
 * TS2835s across the package). The failure is quiet: the exported CONST
 * arrives as an `error` type and every `.map(f => f.name)` downstream becomes
 * an unchecked member access, which is exactly the implicit-any the house
 * rules forbid.
 *
 * Re-declaring the type here restores real checking at every Connect call site
 * without touching a package a dozen other apps already build against. If the
 * package's resolution is ever fixed, this collapses back to a plain re-export.
 */
export const FEATURES = ANNOUNCED_FEATURES as WhatsNewFeature[]

/**
 * The set the current three displaced.
 *
 * The pop-up and the sign-in band show `FEATURES` only — three is the size
 * those surfaces are designed at, and a six-row interruption is a menu rather
 * than an announcement. The permanent "what's new" page carries these below the
 * current three, because a venue that has not switched Wallets on yet is not
 * helped by it vanishing the week something newer ships.
 */
export const PREVIOUS = PREVIOUS_FEATURES as WhatsNewFeature[]

/**
 * Where each announced feature goes INSIDE the product.
 *
 * The content — names, one-liners, figures — is shared with the sign-in and
 * the marketing homepage via `tailwind-v4/blocks/feature-announcement`, so
 * there is one place to reword it. The destinations are Connect's alone, hence
 * this map rather than an `href` on the shared type.
 *
 * Every route here is UNGATED bar one, which is what lets the "What's new"
 * page be visible to everyone without sending anyone to a screen they would
 * then be refused at. Three shapes of destination sit behind that:
 *
 *   • The 2026-09 three point at the REAL surfaces, because all three are
 *     switched on and self-serve. Smart Seating's own page is its teaser (the
 *     replay runs read-only against your own book), online ordering opens on
 *     Ordering > Menus (where each menu's online-ordering switch lives), and
 *     the loyalty index is where a scheme's Square-linked rewards are set up.
 *     Ordering is the exception to "ungated": it carries the `nav.ordering`
 *     role gate (not front-of-house or IT support). It used to point at
 *     Square > Table Ordering, which is retired and redirects there anyway.
 *   • Wallets and Ticketed Events were released in October 2026, so they
 *     point at their REAL settings pages too. Both are venue-scoped, so the
 *     org-level path lands on the house venue-picker gate.
 *   • Phone bookings (Voice AI) keeps its upsell write-up, because it remains
 *     a pilot: there is no real surface to send a customer to.
 *
 * Smart Seating is venue-scoped in reality (the replay reads one venue's
 * book), so its org-level path lands on the house venue-picker gate rather
 * than guessing a serial. Ordering menus are an org library, so that one
 * needs no venue.
 *
 * Keyed by the stable `id`, never the display name, so copy can change freely.
 */
export const FEATURE_ROUTES: Record<WhatsNewFeature['id'], string> = {
  'phone-bookings': 'bookings/upsell/voice',
  'smart-seating': 'bookings/optimisation',
  'square-rewards': 'loyalty',
  'table-ordering': 'ordering/menus',
  'ticketed-events': 'bookings/settings/ticketed-events',
  wallets: 'bookings/settings/wallet',
}

export const featureHref = (orgId: string, id: WhatsNewFeature['id']) =>
  `/${orgId}/${FEATURE_ROUTES[id]}`

/**
 * Where "Speak to the team" goes.
 *
 * The marketing site's sales form, not `mailto:sales@stampede.ai` (which the
 * older upsell pages still use): the form routes to Slack Inbound and creates
 * the HubSpot record, whereas a mailto from inside a web app opens whatever
 * mail client the machine happens to have configured and is untracked.
 *
 * `cta=` rather than `utm_source=` — the site merges stored acquisition UTMs
 * with current ones and lets current win, so a UTM here would overwrite how
 * that customer originally found Stampede. The placement id is registered in
 * `apps/wir/src/lib/cta-links.ts`.
 */
export const SALES_HREF = 'https://stampede.ai/f/sales?cta=connect_whats_new'
